import AsyncButton from '@components/button/AsyncButton';
import SmartDateRangePicker from '@components/input/SmartDateRangePicker';
import SidebarLayout from '@components/layout/SidebarLayout';
import Yard from '@models/Yard';
import SessionService from '@services/SessionService';
import Axios from 'axios';
import dayjs from 'dayjs';
import * as React from 'react';
import env from '../../config/env';

export interface IYardAgentsExportPanelProps {
  yard: Yard
}

interface IState {
  from: Date | null
  to: Date | null
  loading: boolean
  error: string | null
}

export default class YardAgentsExportPanel extends React.Component<IYardAgentsExportPanelProps, IState> {

  constructor(props: IYardAgentsExportPanelProps) {
    super(props);
    this.state = {
      ...this.currentMonthRange(),
      loading: false,
      error: null,
    };
  }

  currentMonthRange = () => ({
    from: dayjs().startOf('month').toDate(),
    to:   dayjs().endOf('month').toDate(),
  })

  currentWeekRange = () => {
    const now = dayjs();
    const daysFromMonday = (now.day() + 6) % 7;
    const monday = now.subtract(daysFromMonday, 'day').startOf('day');
    return {
      from: monday.toDate(),
      to:   monday.add(6, 'day').endOf('day').toDate(),
    };
  }

  yardFullRange = () => ({
    from: this.props.yard.startAt,
    to:   this.props.yard.endAt,
  })

  applyPreset = (range: { from: Date, to: Date }) => this.setState({ ...range, error: null });

  onPeriodChange = ([from, to]: [Date, Date]) => this.setState({ from, to, error: null });

  submit = async () => {
    const { from, to } = this.state;
    if (!from || !to) {
      this.setState({ error: "Sélectionnez une période." });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const url = env.protocol + env.apiUrl + `api/yards/${this.props.yard.id}/export/agents`;
      const token = SessionService.getToken();
      const response = await Axios.get(url, {
        params: {
          from: dayjs(from).format('YYYY-MM-DD'),
          to:   dayjs(to).format('YYYY-MM-DD'),
        },
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `CHIFFRES AGENTS - ${this.props.yard.name} - ${dayjs(from).format('DD-MM-YYYY')}_${dayjs(to).format('DD-MM-YYYY')}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (_e) {
      this.setState({ error: "Erreur lors de la génération du fichier." });
    } finally {
      this.setState({ loading: false });
    }
  }

  render() {
    const { from, to, loading, error } = this.state;
    const { yard } = this.props;
    const hasYardPeriod = !!(yard?.startAt && yard?.endAt);

    return (
      <SidebarLayout
        title="Exporter les chiffres par agent"
        bottomArea={
          <AsyncButton className="btn btn-primary w-100 btn-lg" loading={loading} onClick={this.submit}>
            Exporter
          </AsyncButton>
        }
      >
        <div className="fadeInDown">
          <div className="mb-3">
            <div className="text-s text-secondary mb-2">Raccourcis</div>
            <div className="d-flex flex-wrap">
              <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={() => this.applyPreset(this.currentMonthRange())}>Mois en cours</button>
              <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={() => this.applyPreset(this.currentWeekRange())}>Semaine en cours</button>
              {hasYardPeriod && (
                <button type="button" className="btn btn-sm btn-outline-primary me-2 mb-2" onClick={() => this.applyPreset(this.yardFullRange())}>Durée du chantier</button>
              )}
            </div>
          </div>

          <SmartDateRangePicker containerClassName="mb-3" onChange={this.onPeriodChange} from={from} to={to} />

          {error && <div className="alert alert-danger">{error}</div>}
        </div>
      </SidebarLayout>
    );
  }
}
